// Shared, dependency-free styling helpers for the report PDFs (jsPDF + jspdf-autotable).
// Gives every report a consistent modern look: a green header band, clean borderless tables
// with zebra rows and a branded head, colour-coded scores, and a page footer.
// Colours mirror the on-screen reports so the PDF reads as the same product.

const GREEN = [47, 138, 69];
const INK = [24, 32, 29];
const SOFT = [84, 99, 92];
const FAINT = [135, 148, 140];
const LINE = [227, 233, 229];
const SEC_FILL = [231, 244, 234];
const ZEBRA = [244, 249, 246];
const DOWN = [193, 73, 91];
// warm(low) → green(high) ramp, same anchors as the on-screen rating bars
const RAMP = [[196, 69, 54], [233, 162, 59], [167, 201, 87], [99, 149, 74], [47, 107, 79]];

function scoreColor(v, max) {
  const p = max ? Math.max(0, Math.min(1, Number(v) / max)) : 0;
  const x = p * (RAMP.length - 1);
  const k = Math.min(RAMP.length - 2, Math.floor(x));
  const f = x - k;
  return RAMP[k].map((c, i) => Math.round(c + (RAMP[k + 1][i] - c) * f));
}
function deltaColor(d) { return d >= 0 ? GREEN.slice() : DOWN.slice(); }

// Colour for a value within [minV, maxV] along the warm→green ramp (matches on-screen bars).
function rampColor(v, minV, maxV) {
  if (maxV <= minV) return RAMP[2].slice();
  const p = (Number(v) - minV) / (maxV - minV) * (RAMP.length - 1);
  const k = Math.min(RAMP.length - 2, Math.max(0, Math.floor(p)));
  const f = p - k;
  return RAMP[k].map((c, i) => Math.round(c + (RAMP[k + 1][i] - c) * f));
}

// Parse a colour string into [r,g,b]. Rating buckets give "rgb(r,g,b)"; categorical buckets
// (multiple-choice / non-radio questions) give hex like "#3b6fd4" — handle both, or the
// categorical bars fall back to grey in the PDF.
function parseRgb(s) {
  if (!s) return [150, 150, 150];
  const str = String(s).trim();
  let m = /rgb\((\d+),\s*(\d+),\s*(\d+)\)/i.exec(str);
  if (m) return [+m[1], +m[2], +m[3]];
  m = /^#([0-9a-f]{6})$/i.exec(str);
  if (m) return [parseInt(m[1].slice(0, 2), 16), parseInt(m[1].slice(2, 4), 16), parseInt(m[1].slice(4, 6), 16)];
  m = /^#([0-9a-f]{3})$/i.exec(str);
  if (m) return [parseInt(m[1][0] + m[1][0], 16), parseInt(m[1][1] + m[1][1], 16), parseInt(m[1][2] + m[1][2], 16)];
  return [150, 150, 150];
}

// Full title band on the current page. Returns the Y to start content at.
function header(doc, opts) {
  const o = opts || {};
  const W = doc.internal.pageSize.getWidth();
  doc.setFillColor(GREEN[0], GREEN[1], GREEN[2]);
  doc.rect(0, 0, W, 26, 'F');
  doc.setTextColor(255, 255, 255);
  try { doc.setFont('DejaVuSans', 'bold'); } catch (e) { doc.setFont('DejaVuSans'); }
  doc.setFontSize(13);
  doc.text(String(o.title || ''), 12, 12, { maxWidth: W - 60 });
  doc.setFontSize(11);
  doc.text('GooQuest', W - 12, 11, { align: 'right' });
  if (o.right) {
    doc.setFont('DejaVuSans', 'normal'); doc.setFontSize(8); doc.setTextColor(224, 242, 231);
    doc.text(String(o.right), W - 12, 17, { align: 'right' });
  }
  if (o.subtitle) {
    doc.setFont('DejaVuSans', 'normal'); doc.setFontSize(9); doc.setTextColor(224, 242, 231);
    doc.text(String(o.subtitle), 12, 19.5, { maxWidth: W - 70 });
  }
  return 34;
}

// Slim top strip (drawn on every page via autoTable didDrawPage, so page 2+ stay branded).
function topStrip(doc) {
  const W = doc.internal.pageSize.getWidth();
  doc.setFillColor(GREEN[0], GREEN[1], GREEN[2]);
  doc.rect(0, 0, W, 8, 'F');
}

// Page footer with wordmark + "x / y". Call once, right before doc.save().
function footer(doc) {
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const n = doc.internal.getNumberOfPages();
  for (let i = 1; i <= n; i++) {
    doc.setPage(i);
    doc.setFont('DejaVuSans', 'normal'); doc.setFontSize(8);
    doc.setTextColor(FAINT[0], FAINT[1], FAINT[2]);
    doc.text('GooQuest', 12, H - 8);
    doc.text(i + ' / ' + n, W - 12, H - 8, { align: 'right' });
  }
}

// Modern autoTable options — borderless, branded head, zebra rows, slim strip on each page.
function modernOptions(over) {
  return Object.assign({
    theme: 'striped',
    styles: { font: 'DejaVuSans', fontSize: 8.5, cellPadding: 3, textColor: INK, lineWidth: 0, overflow: 'linebreak' },
    headStyles: { font: 'DejaVuSans', fontStyle: 'bold', fillColor: GREEN, textColor: [255, 255, 255], fontSize: 8 },
    alternateRowStyles: { fillColor: ZEBRA },
    margin: { top: 16, left: 12, right: 12, bottom: 16 },
    // autoTable passes the doc on the hook data; draw the slim branded strip on every page.
    didDrawPage: (data) => { try { topStrip(data.doc); } catch (e) { /* ignore */ } },
  }, over || {});
}

export default {
  GREEN, INK, SOFT, FAINT, LINE, SEC_FILL, ZEBRA, DOWN,
  scoreColor, deltaColor, rampColor, parseRgb, header, footer, topStrip, modernOptions,
};
